import { formatBaht } from '@xver/utils/number';
import { Loader } from '@mantine/core';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { CreditSummaryResponse } from '@xver/@types/bill';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { useHorizontalOverflow } from '@xver/hooks/useHorizontalOverflow';

interface CreditBreakdownSectionProps {
    summary?: CreditSummaryResponse;
    isLoading: boolean;
    isError: boolean;
}

function KindPill({ kind }: { kind: 'coupon' | 'trial' }) {
    const { t } = useTranslation('billings');
    const color =
        kind === 'coupon'
            ? 'bg-blue-900/40 text-blue-300 border-blue-800/60 light:bg-blue-100 light:text-blue-700 light:border-blue-300'
            : 'bg-purple-900/40 text-purple-300 border-purple-800/60 light:bg-purple-100 light:text-purple-700 light:border-purple-300';
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border-[0.5px] ${color}`}
        >
            {t(`creditSummary.kind.${kind}`)}
        </span>
    );
}

export default function CreditBreakdownSection({
    summary,
    isLoading,
    isError,
}: CreditBreakdownSectionProps) {
    const { t } = useTranslation('billings');
    const grantsRef = useRef<HTMLDivElement>(null);
    const grantsOverflowing = useHorizontalOverflow(grantsRef, !!summary?.grants.length);

    return (
        <DialogBox
            label={t('creditSummary.breakdown.title')}
            description={t('creditSummary.breakdown.description')}
            classNames="p-4"
        >
            {isError ? (
                <div className="w-full flex items-center justify-center py-8 text-sm text-fg/68">
                    {t('creditSummary.error')}
                </div>
            ) : isLoading || !summary ? (
                <div className="w-full flex items-center justify-center py-8">
                    <Loader size="sm" color="var(--fg)" />
                </div>
            ) : (
                <div className="w-full flex flex-col gap-4">
                    <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {(
                            [
                                ['topUp', summary.credit.topUp],
                                ['coupon', summary.credit.coupon],
                                ['trial', summary.credit.trial],
                            ] as const
                        ).map(([key, amount]) => (
                            <div
                                key={key}
                                className="flex flex-col gap-1 rounded border-[0.5px] border-line bg-surface-1 p-3"
                            >
                                <span className="text-sm text-fg/68">
                                    {t(`creditSummary.breakdown.${key}`)}
                                </span>
                                <span className="text-2xl font-bold">{formatBaht(amount)} ฿</span>
                            </div>
                        ))}
                    </div>

                    {summary.grants.length > 0 && (
                        <div
                            ref={grantsRef}
                            role="region"
                            aria-label={t('creditSummary.grants.caption')}
                            tabIndex={grantsOverflowing ? 0 : undefined}
                            className="relative w-full overflow-x-auto focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg"
                        >
                            <table className="w-full text-sm">
                                <caption className="sr-only">
                                    {t('creditSummary.grants.caption')}
                                </caption>
                                <thead>
                                    <tr className="text-left text-fg/68 border-b-[0.5px] border-line">
                                        <th className="py-2 pr-3 font-medium">
                                            {t('creditSummary.grants.type')}
                                        </th>
                                        <th className="py-2 pr-3 font-medium text-right">
                                            {t('creditSummary.grants.remaining')}
                                        </th>
                                        <th className="py-2 pr-3 font-medium text-right">
                                            {t('creditSummary.grants.granted')}
                                        </th>
                                        <th className="py-2 pr-3 font-medium">
                                            {t('creditSummary.grants.appliesTo')}
                                        </th>
                                        <th className="py-2 font-medium">
                                            {t('creditSummary.grants.expires')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary.grants.map((grant, index) => (
                                        <tr
                                            key={index}
                                            className="border-b-[0.5px] border-line/60 last:border-b-0"
                                        >
                                            <td className="py-2 pr-3">
                                                <KindPill kind={grant.kind} />
                                            </td>
                                            <td className="py-2 pr-3 text-right font-semibold">
                                                {formatBaht(grant.amountRemaining)} ฿
                                            </td>
                                            <td className="py-2 pr-3 text-right text-fg/68">
                                                {formatBaht(grant.amountGranted)} ฿
                                            </td>
                                            <td className="py-2 pr-3 text-fg/80">
                                                {grant.allowedProducts &&
                                                grant.allowedProducts.length > 0
                                                    ? grant.allowedProducts.join(', ')
                                                    : t('creditSummary.grants.allProducts')}
                                            </td>
                                            <td className="py-2 text-fg/80">
                                                {grant.expiresAt
                                                    ? new Date(grant.expiresAt).toLocaleDateString(
                                                          'th-TH'
                                                      )
                                                    : t('creditSummary.grants.noExpiry')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </DialogBox>
    );
}
